import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Fragment, ReactNode, useState } from 'react';

import {
  PageStatus,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import type {
  IBlacklistLabels,
  IRulesLabels,
  IWhitelistLabels,
} from '@app/dashboard/types/innerLayout/risk_center/single-details';
import { useLangStore } from '@app/dashboard/store/lang';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  API_SINGLE_DETAILS_MAP,
  CONFIRMATION_MODALS_CONFIG,
  RISK_CENTER_TYPES,
  SINGLE_DETAILS_LABELS,
} from '@app/dashboard/constants/risk_center';
import type { TRiskCenterTypes } from '@app/dashboard/types/innerLayout/risk_center';

import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';

import Header from './Header';
import RiskCenterStatus from './RiskCenterStatus';

import styles from './SingleRiskCenter.module.scss';

const EMPTY_VALUE = '-';

const SingleRiskCenter = () => {
  const { t } = useTranslation();
  const { id, type } = useParams<{ id: string; type: TRiskCenterTypes }>();
  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();

  const [modalAction, setModalAction] = useState<
    'delete' | 'deactivate' | 'activate' | null
  >(null);

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const queryFunction = API_SINGLE_DETAILS_MAP[type as TRiskCenterTypes];

  const {
    isError,
    data: { data: riskCenterData } = {},
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_RISK_ITEM_DETAILS, lang, id, type],
    queryFn: () => queryFunction(id as string),
    enabled: !!id && !!type,
    refetchOnMount: 'always',
  });

  const detailsConfig = SINGLE_DETAILS_LABELS(t)[type as TRiskCenterTypes];
  const modalsConfig = modalAction
    ? CONFIRMATION_MODALS_CONFIG(type as TRiskCenterTypes, t)[modalAction]
    : null;

  const detailsBoxRenderer = ({
    box = undefined,
    height,
    noOfRows,
    key,
  }: {
    box?: ReactNode;
    height: string;
    noOfRows: number;
    key: number | string;
  }): ReactNode => {
    return isFetching ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      box && <Fragment key={key}>{box}</Fragment>
    );
  };

  const allBoxes = [
    riskCenterData &&
      detailsBoxRenderer({
        box: (
          <InfoCard
            title={detailsConfig.title}
            noOfColumns={2}
            pairsList={[
              {
                label: detailsConfig.labels.idLabel,
                value: riskCenterData?.id || EMPTY_VALUE,
              },
              {
                label: detailsConfig.labels.activeLabel,
                value:
                  riskCenterData?.active !== undefined
                    ? <RiskCenterStatus status={riskCenterData?.active} />
                    : EMPTY_VALUE,
              },
              {
                label: detailsConfig.labels.createdAtLabel,
                value:
                  formatToLocalizedDateTime(riskCenterData?.created, lang) ||
                  EMPTY_VALUE,
              },
              ...(type === RISK_CENTER_TYPES.BLACKLIST
                ? [
                    {
                      label: (detailsConfig.labels as IBlacklistLabels)
                        .blacklistTypeLabel,
                      value:
                        (lang === 'en'
                          ? riskCenterData?.blacklist_type
                          : riskCenterData?.blacklist_type_ar) || EMPTY_VALUE,
                    },
                    {
                      label: (detailsConfig.labels as IBlacklistLabels)
                        .dataLabel,
                      value: riskCenterData?.blacklist_data || EMPTY_VALUE,
                    },
                    {
                      label: (detailsConfig.labels as IBlacklistLabels)
                        .reasonLabel,
                      value: riskCenterData?.reason || EMPTY_VALUE,
                    },
                  ]
                : type === RISK_CENTER_TYPES.WHITELIST
                ? [
                    {
                      label: (detailsConfig.labels as IWhitelistLabels)
                        .whitelistTypeLabel,
                      value:
                        (lang === 'en'
                          ? riskCenterData?.whitelist_type
                          : riskCenterData?.whitelist_type_ar) || EMPTY_VALUE,
                    },
                    {
                      label: (detailsConfig.labels as IWhitelistLabels)
                        .dataLabel,
                      value: riskCenterData?.whitelist_data || EMPTY_VALUE,
                    },
                    {
                      label: (detailsConfig.labels as IWhitelistLabels)
                        .reasonLabel,
                      value: riskCenterData?.reason || EMPTY_VALUE,
                    },
                  ]
                : type === RISK_CENTER_TYPES.RULES
                ? [
                    {
                      label: (detailsConfig.labels as IRulesLabels).ruleLabel,
                      value:
                        lang === 'en'
                          ? riskCenterData?.rule?.description
                          : riskCenterData?.rule?.description_ar || EMPTY_VALUE,
                    },
                    {
                      label: (detailsConfig.labels as IRulesLabels)
                        .priorityLabel,
                      value:
                        riskCenterData?.priority !== undefined
                          ? riskCenterData?.priority
                          : EMPTY_VALUE,
                    },
                  ]
                : []),
            ]}
          />
        ),
        noOfRows: 1,
        height: '24rem',
        key: t('innerLayout.chargeBacks.singleChargeBacks.title'),
      }),
  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const content = (
    <>
      {isMobile ? (
        <>
          {col1Boxes}
          {col2Boxes}
        </>
      ) : (
        [col1Boxes, col2Boxes].map((colContent, index) => (
          <div
            className={styles['single-risk-center__content-col']}
            key={`single-risk-center-col-${index}`}
          >
            {colContent}
          </div>
        ))
      )}
    </>
  );

  return isError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={t('innerLayout.errors.dataRetrievalErr')}
      icon={CircleExclamation}
    />
  ) : (
    <div className={styles['single-risk-center']}>
      {isFetching ? (
        <SkeletonBase height="2.5rem" />
      ) : (
        <Header
          riskCenterData={riskCenterData}
          modalAction={modalAction}
          modalsConfig={modalsConfig}
          setModalAction={setModalAction}
        />
      )}
      {isFetching ? (
        <SkeletonLoader
          type="bordered_box"
          noOfRows={5}
          height="17.5rem"
          key="single-risk-center-skeleton"
        />
      ) : (
        <div className={styles['single-risk-center__content']}>{content}</div>
      )}
    </div>
  );
};

export default SingleRiskCenter;
